import React, { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import { lensChangeFailure } from '~/hooks/apis/reporting/useLensChange';

import type { LensResponse } from '~/types/leLens';

export type PublicationConfirmation = 'publish' | 'discard' | 'takeover';

type Props = {
  action: PublicationConfirmation | null;
  detail: LensResponse;
  onClose: () => void;
  onConfirm: (action: PublicationConfirmation) => Promise<void>;
  pending: boolean;
};

const TITLES: Record<PublicationConfirmation, string> = {
  publish: 'Publish changes?',
  discard: 'Discard the shared draft?',
  takeover: 'Take over editing?',
};

export default function LensPublicationDialog({ action, detail, onClose, onConfirm, pending }: Props) {
  const [error, setError] = useState<string>();
  const close = () => {
    if (pending) return;
    setError(undefined);
    onClose();
  };
  const confirm = async () => {
    if (!action || pending) return;
    setError(undefined);
    try {
      await onConfirm(action);
      onClose();
    } catch (failure) {
      setError(lensChangeFailure(failure));
    }
  };
  return (
    <Dialog aria-labelledby="lens-publication-title" fullWidth maxWidth="sm" onClose={close} open={Boolean(action)}>
      <DialogTitle id="lens-publication-title">
        {action === 'publish' && detail.publication?.publishedVersion === null
          ? 'Publish this report?'
          : action
            ? TITLES[action]
            : ''}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {action === 'publish' ? (
          <>
            <DialogContentText>
              Everyone with access will see{' '}
              {detail.publication?.publishedVersion === null ? 'this report' : 'these changes'}.
            </DialogContentText>
            {detail.certification?.badge.kind === 'certified' ? (
              <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                Certification is re-checked when you publish.
              </Typography>
            ) : null}
          </>
        ) : null}
        {action === 'discard' ? (
          <DialogContentText>
            Removes every unpublished change, including other editors’ work. The published report is unaffected.
          </DialogContentText>
        ) : null}
        {action === 'takeover' ? (
          <DialogContentText>
            You’ll continue from {detail.publication?.editorEmail}’s saved changes, and they’ll stop editing.
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={close}>
          {action === 'publish' ? 'Keep editing' : action === 'discard' ? 'Keep draft' : 'Cancel'}
        </Button>
        <Button disabled={pending} onClick={() => void confirm()} variant="contained">
          {pending
            ? 'Saving…'
            : action === 'publish'
              ? 'Publish'
              : action === 'discard'
                ? 'Discard shared draft'
                : 'Take over editing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
